import { useState } from "react";
import { Button, Form, Input, Modal, Switch, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ListPage } from "../../components/ListPage";
import { useApi } from "../../helpers/useApi";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { Supplier } from "../../api-models";

export default function SuppliersPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Suppliers.Write");
  const { request } = useApi();
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [reloadKey, setReloadKey] = useState(0);

  function openEdit(s: Supplier) {
    setEditing(s);
    form.setFieldsValue(s);
    setOpen(true);
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/suppliers/${editing.id}`, data: { ...editing, ...values } });
    else await request({ method: "POST", url: "/suppliers", data: values });
    setOpen(false);
    setReloadKey((k) => k + 1);
  }

  return (
    <>
      <ListPage<Supplier>
        key={reloadKey}
        title="Suppliers"
        endpoint="/suppliers"
        searchPlaceholder="Search suppliers..."
        actions={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                form.resetFields();
                setOpen(true);
              }}
            >
              New supplier
            </Button>
          )
        }
        columns={[
          { title: "Name", dataIndex: "name", sorter: true },
          { title: "TIN", dataIndex: "tin", sorter: true },
          { title: "Contact", dataIndex: "contactPerson", sorter: true },
          { title: "Phone", dataIndex: "phone" },
          { title: "Email", dataIndex: "email", sorter: true },
          { title: "Status", dataIndex: "active", sorter: true, render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
          ...(canWrite ? [{ title: "", key: "edit", render: (_: unknown, r: Supplier) => <Button type="link" onClick={() => openEdit(r)}>Edit</Button> }] : []),
        ]}
      />
      <Modal title={editing ? "Edit supplier" : "New supplier"} open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tin" label="TIN">
            <Input />
          </Form.Item>
          <Form.Item name="contactPerson" label="Contact person">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          {editing && (
            <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
